import { DeleteOutlined, MessageOutlined, NotificationOutlined, PaperClipOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Table, Tag, Tooltip, Typography, type TableProps } from "antd";
import dayjs from "dayjs";

import "dayjs/locale/pl";

import React from "react";
import isEqual from "lodash.isequal";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type PlannedTasksSequenceMetadata } from "@fitness-app/data-models/entities/ProgramAutomation";
import { type TeamMemberWithUser } from "@fitness-app/data-models/entities/TeamMember";

import { usePermission } from "~/hooks/usePermission";
import { useDeleteScheduledMessagesSequence } from "~/modules/Trainee/hooks/useDeleteScheduledMessagesSequence";
import {
  useScheduledMessagesSequence,
  type ScheduledMessagesSequenceWithAuthor,
} from "~/modules/Trainee/hooks/useScheduledMessagesSequence";
import { createTrainerOptionsByUserId } from "./helpers/createTrainerOptionsByUserId";

interface ScheduledMessagesTableProps {
  trainers: TeamMemberWithUser[];
}

export const ScheduledMessagesTable = ({ trainers }: ScheduledMessagesTableProps): React.ReactElement => {
  const { t } = useTranslation(["trainees", "common"]);
  const { data, isLoading, addedByFilter, setAddedByFilter } = useScheduledMessagesSequence();
  // Kolumna „Trener" ma sens dopiero, gdy widać cudze wiadomości.
  const canFilterByAuthor = usePermission(PermissionKey.CHAT_WRITE, PermissionScope.TEAM);
  const { deleteMutation } = useDeleteScheduledMessagesSequence();

  const handleTableChange: TableProps<ScheduledMessagesSequenceWithAuthor>["onChange"] = (_pagination, filters) => {
    const newAddedByFilter = filters.addedBy as string[] | null;
    if (!isEqual(addedByFilter, newAddedByFilter)) {
      setAddedByFilter(newAddedByFilter?.length ? newAddedByFilter : null);
    }
  };

  const getAuthorName = (record: ScheduledMessagesSequenceWithAuthor): string => {
    if (record.metadata?.authorName) {
      return record.metadata.authorName;
    }
    if (record.author) {
      return `${record.author.firstName} ${record.author.lastName}`;
    }
    if (record.teamMember) {
      return `${record.teamMember.firstName} ${record.teamMember.lastName}`;
    }
    return "-";
  };

  const getMessagePreview = (metadata: PlannedTasksSequenceMetadata | null): string => {
    if (!metadata?.messages?.length) {
      return "-";
    }
    return truncate(metadata.messages.join(" \n\n "), { length: 50 });
  };

  const getFiltersDescription = (metadata: PlannedTasksSequenceMetadata | null): string | null => {
    if (!metadata?.filters?.length) {
      return null;
    }

    return metadata.filters
      .map((filter) => {
        if (filter.tags?.length) {
          return `Tagi: ${filter.tags.join(", ")}`;
        }
        return filter.type;
      })
      .join("; ");
  };

  return (
    <Table<ScheduledMessagesSequenceWithAuthor>
      rowKey="id"
      scroll={{ x: true }}
      loading={isLoading}
      dataSource={data}
      pagination={{ pageSize: 20 }}
      onChange={handleTableChange}
      locale={{
        emptyText: "Brak zaplanowanych wiadomości grupowych",
      }}
    >
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="id"
        dataIndex="id"
        title="ID"
        width={120}
        ellipsis
        render={(id: string) => (
          <Typography.Paragraph copyable={{ tooltips: [id, t("common:copied")], text: id }} style={{ marginBottom: 0 }}>
            {truncate(id, { length: 10 })}
          </Typography.Paragraph>
        )}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="scheduledAt"
        dataIndex="scheduledAt"
        title="Data wysyłki"
        width={180}
        render={(scheduledAt: string) => <span>{dayjs(scheduledAt).format("DD.MM.YYYY HH:mm")}</span>}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="numberOfClients"
        dataIndex="numberOfClients"
        title="Liczba klientów"
        width={140}
        align="center"
        render={(count: number) => <Tag color="blue">{count}</Tag>}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="numberOfSequences"
        dataIndex="metadata"
        title="Liczba sekwencji"
        width={140}
        align="center"
        render={(metadata: PlannedTasksSequenceMetadata | null) => {
          const count = metadata?.numberOfSequences ?? 1;
          const dates = metadata?.dates;

          if (count > 1 && dates?.length) {
            return (
              <Tooltip
                title={
                  <div>
                    {dates.map((date, index) => (
                      <div key={index}>{dayjs(date.sendDate).locale("pl").format("dddd, DD.MM.YYYY HH:mm")}</div>
                    ))}
                  </div>
                }
              >
                <Tag color="purple" style={{ cursor: "pointer" }}>
                  {count}
                </Tag>
              </Tooltip>
            );
          }

          return <Tag>{count}</Tag>;
        }}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="channel"
        dataIndex="metadata"
        title="Kanał"
        width={130}
        align="center"
        render={(metadata: PlannedTasksSequenceMetadata | null) =>
          // Legacy rows (before TRE-801) carry no flag — those always went to the regular chat.
          metadata?.sendToDefaultChannel === false ? (
            // Conditional routing, not a delivery guarantee — a client without the
            // announcement channel gets the message on her regular chat.
            <Tooltip title="Trafi na kanał ogłoszeniowy; klientki bez tego kanału dostaną wiadomość na zwykłym czacie">
              <Tag color="gold" icon={<NotificationOutlined />}>
                Ogłoszeniowy
              </Tag>
            </Tooltip>
          ) : (
            <Tag icon={<MessageOutlined />}>Czat</Tag>
          )
        }
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="messages"
        dataIndex="metadata"
        title="Wiadomość"
        ellipsis
        render={(metadata: PlannedTasksSequenceMetadata | null) => {
          const preview = getMessagePreview(metadata);
          return (
            <Tooltip
              title={[
                ...(metadata?.messages ?? []).map((item, index) => <div key={`message-${index}`}>- {item}</div>),
                ...(metadata?.attachments ?? []).map((file) => (
                  <div key={file.uid}>
                    <PaperClipOutlined /> {file.shortName}
                  </div>
                )),
              ]}
            >
              <span>
                {metadata?.attachments?.length ? (
                  <span className="mr-1">
                    <PaperClipOutlined /> {metadata.attachments.length}
                  </span>
                ) : null}
                {preview}
              </span>
            </Tooltip>
          );
        }}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="filters"
        dataIndex="metadata"
        title="Filtry"
        width={200}
        render={(metadata: PlannedTasksSequenceMetadata | null) => {
          const filters = getFiltersDescription(metadata);
          return filters ? <Tag>{filters}</Tag> : "-";
        }}
      />
      {canFilterByAuthor && (
        <Table.Column<ScheduledMessagesSequenceWithAuthor>
          key="addedBy"
          dataIndex="addedBy"
          title={t("tableHeader.trainer")}
          width={180}
          filterMultiple
          filterSearch
          filters={createTrainerOptionsByUserId(trainers, t, true)}
          filteredValue={addedByFilter}
          render={(addedBy: string | null) => {
            if (!addedBy) return "-";
            const trainer = trainers.find((t) => t.userId === addedBy);
            return trainer ? <Tag color="magenta">{`${trainer.firstName} ${trainer.lastName}`}</Tag> : "-";
          }}
        />
      )}
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="author"
        title="Autor wiadomości"
        width={180}
        render={(_, record) => {
          const authorName = getAuthorName(record);
          return <Tag color="magenta">{authorName}</Tag>;
        }}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="createdAt"
        dataIndex="createdAt"
        title="Utworzono"
        width={180}
        render={(createdAt: string) => <span>{dayjs(createdAt).format("DD.MM.YYYY HH:mm")}</span>}
      />
      <Table.Column<ScheduledMessagesSequenceWithAuthor>
        key="actions"
        title="Opcje"
        width={100}
        align="center"
        render={(_, record) => (
          <Popconfirm
            title="Czy na pewno chcesz usunąć zaplanowane wiadomości?"
            description="Ta operacja usunie wszystkie zaplanowane wiadomości w tej sekwencji."
            okText="Tak"
            cancelText="Nie"
            placement="left"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={deleteMutation.isLoading} />
          </Popconfirm>
        )}
      />
    </Table>
  );
};
